import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { Router } from '@angular/router';
import { MenuGroupService, CreateMenuGroupRequest } from '../../../services/menu-group.service';
import { ModuleService } from '../../../services/module.service';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { LoggerService } from '../../../core/services/logger.service';

@Component({
    selector: 'app-create-menu-group-page',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatInputModule,
        MatSnackBarModule,
        MatCardModule,
    ],
    templateUrl: './create-menu-group-page.component.html',
    styleUrl: './create-menu-group-page.component.scss',
})
export class CreateMenuGroupPageComponent {
    form: FormGroup;
    isLoading = false;

    constructor(
        private fb: FormBuilder,
        private menuGroupService: MenuGroupService,
        private moduleService: ModuleService,
        private snackBar: MatSnackBar,
        private logger: LoggerService,
        private router: Router
    ) {
        this.form = this.fb.group({
            nombre: ['', [Validators.required, Validators.maxLength(100)]],
            orden: [0, [Validators.required, Validators.min(0)]],
            integration: [false],
            url_token: ['', [Validators.maxLength(255)]],
            api_key: ['', [Validators.maxLength(255)]],
            session_api_key: ['', [Validators.maxLength(255)]],
        });

        this.form.get('integration')?.valueChanges.subscribe((isIntegration: boolean) => {
            if (!isIntegration) {
                this.form.get('url_token')?.setValue('');
                this.form.get('api_key')?.setValue('');
                this.form.get('session_api_key')?.setValue('');
            }
        });
    }

    getError(field: string): string {
        const c = this.form.get(field);
        if (!c) return '';
        if (c.hasError('required')) return 'Este campo es requerido';
        if (c.hasError('maxlength')) return `Máximo ${c.errors?.['maxlength']?.requiredLength} caracteres`;
        if (c.hasError('min')) return 'El valor debe ser mayor o igual a 0';
        return 'Campo inválido';
    }

    markTouched(): void {
        Object.keys(this.form.controls).forEach((k) => this.form.get(k)?.markAsTouched());
    }

    onSubmit(): void {
        if (!this.form.valid) {
            this.markTouched();
            return;
        }
        this.isLoading = true;
        const v = this.form.value;
        const body: CreateMenuGroupRequest = {
            nombre: v.nombre.trim(),
            orden: v.orden ?? 0,
            integration: !!v.integration,
            url_token: v.integration ? (v.url_token?.trim() || null) : null,
            ...(v.integration && v.api_key?.trim() ? { api_key: v.api_key.trim() } : {}),
            ...(v.integration && v.session_api_key?.trim()
                ? { session_api_key: v.session_api_key.trim() }
                : {}),
        };
        this.menuGroupService
            .createMenuGroup(body)
            .pipe(
                catchError((err) => {
                    this.logger.error('Error al crear grupo de menú', err as Error, 'CreateMenuGroupPageComponent');
                    this.snackBar.open(err.error?.message || 'Error al crear el grupo', 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar'],
                    });
                    return of(null);
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((res) => {
                if (res?.success) {
                    this.moduleService.notifyModulesChanged();
                    this.snackBar.open('Grupo de menú creado exitosamente', 'Cerrar', {
                        duration: 3000,
                        panelClass: ['success-snackbar'],
                    });
                    this.router.navigate(['/modules/menu-groups']);
                }
            });
    }

    onCancel(): void {
        this.router.navigate(['/modules/menu-groups']);
    }
}
